import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import DesktopTerminalCard from "../components/DesktopTerminalCard";
import { API, authMe, createTerminal, downloadAgentExport } from "../lib/api";

const INTENT_KEY = "tv_build_intent";
const ACTIVE_KEY = "tv_active_terminal_id";

/**
 * /build — resumes the "save to my terminal / download agent file" flow after
 * Google login.
 *  - human → name the build, then create terminals. The backend splits the
 *    tools by asset class (one terminal per class; multi-asset tools ride along
 *    in each), so a mixed recommendation yields multiple terminals.
 *  - agent → factual JSON/MD/README export (NOT persisted).
 */
export default function Build() {
  const nav = useNavigate();
  const ran = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | name | human | agent | empty | error
  const [created, setCreated] = useState([]); // [{id,title,asset_class,tool_count}]
  const [slugs, setSlugs] = useState([]);
  const [tools, setTools] = useState([]); // [{slug,title,asset_class,key_feature}] for the checklist
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let intent = null;
    try {
      intent = JSON.parse(localStorage.getItem(INTENT_KEY) || "null");
    } catch {
      intent = null;
    }

    (async () => {
      let me = null;
      try {
        me = await authMe();
      } catch {
        me = null;
      }
      if (!me || !me.email) {
        nav("/", { replace: true });
        return;
      }
      if (!intent || !Array.isArray(intent.slugs) || intent.slugs.length === 0) {
        setPhase("empty");
        return;
      }
      setSlugs(intent.slugs);
      if (intent.label) setNameDraft(intent.label);

      if (intent.action === "agent") {
        setPhase("agent");
        try { localStorage.removeItem(INTENT_KEY); } catch { /* ignore */ }
        return;
      }

      // human flow → fetch the recommended tools so the user can pick which
      // ones to actually add (checklist, all selected by default).
      const fetched = await Promise.all(
        intent.slugs.map((s) =>
          axios
            .get(`${API}/tools/by-slug/${encodeURIComponent(s)}`)
            .then((r) => r.data)
            .catch(() => null),
        ),
      );
      const valid = fetched.filter(Boolean);
      setTools(valid);
      setSelected(new Set((valid.length ? valid.map((t) => t.slug) : intent.slugs)));
      setPhase("name");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const toggleTool = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const stripClass = (title, ac) =>
    (title && ac && title.endsWith(` · ${ac}`) ? title.slice(0, -(` · ${ac}`.length)) : title) || title;

  const build = async () => {
    const chosen = tools.length
      ? tools.filter((t) => selected.has(t.slug)).map((t) => t.slug)
      : slugs;
    if (!chosen.length) {
      toast.error("select at least one tool to add");
      return;
    }
    setBusy(true);
    try {
      const data = await createTerminal({ slugs: chosen, title: nameDraft.trim() || undefined });
      const terminals = (data && data.terminals) || [];
      if (!terminals.length) throw new Error("no terminals created");
      setCreated(terminals);
      try {
        localStorage.setItem(ACTIVE_KEY, data.primary_id || terminals[0].id);
        localStorage.setItem("tv_terminal_id", data.primary_id || terminals[0].id);
      } catch { /* ignore */ }
      try { localStorage.removeItem(INTENT_KEY); } catch { /* ignore */ }
      setPhase("human");
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "could not build terminal");
      setPhase("error");
    } finally {
      setBusy(false);
    }
  };

  const dl = async (fmt) => {
    try {
      await downloadAgentExport(slugs, fmt);
    } catch {
      toast.error("export failed — try again");
    }
  };

  const multi = created.length > 1;

  return (
    <div data-testid="build-page">
      <Seo title="save to my terminal" path="/build" noindex />
      <Header />

      <section className="border-b border-black bg-white">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <h1 className="text-[18px] md:text-[20px] font-bold leading-snug">
            {">"} <span className="tv-hl">save to my terminal.</span>
          </h1>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-16 text-[13px]">
        {phase === "loading" && (
          <div className="text-[#888] py-10" data-testid="build-loading">
            loading<span className="tv-blink">_</span>
          </div>
        )}

        {phase === "empty" && (
          <div className="text-[#666] py-10" data-testid="build-empty">
            nothing to build. head back to the{" "}
            <Link to="/" className="tv-link">index</Link> and ask the concierge for
            a few tools first.
          </div>
        )}

        {phase === "error" && (
          <div className="text-[#c00] py-10" data-testid="build-error">
            [error] {errMsg}
            <div className="mt-3 text-[#666]">
              <Link to="/" className="tv-link">← back to the index</Link>
            </div>
          </div>
        )}

        {phase === "name" && (
          <div data-testid="build-name">
            <div className="border-2 border-black p-4">
              <div className="font-bold mb-1">
                {">"}_ pick your tools ({selected.size} of {tools.length || slugs.length} selected)
              </div>
              <p className="text-[#555] leading-snug mb-3">
                Check the tools you want in your terminal. Tools are organized by
                asset class — picks spanning classes split into one terminal per
                class (e.g. Equities, Crypto).
              </p>

              {tools.length > 0 && (
                <div className="mb-3" data-testid="build-tool-checklist">
                  <div className="flex items-center gap-2 text-[11px] text-[#888] mb-2">
                    <button
                      type="button"
                      onClick={() => setSelected(new Set(tools.map((t) => t.slug)))}
                      className="tv-link"
                      data-testid="build-select-all"
                    >
                      select all
                    </button>
                    <span className="text-[#ddd]">·</span>
                    <button
                      type="button"
                      onClick={() => setSelected(new Set())}
                      className="tv-link"
                      data-testid="build-select-none"
                    >
                      none
                    </button>
                  </div>
                  <ul className="list-none p-0 m-0 border border-[#ddd] divide-y divide-[#eee]">
                    {tools.map((t) => {
                      const on = selected.has(t.slug);
                      return (
                        <li key={t.slug}>
                          <label
                            className={`flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-[#f7f7f7] transition-colors ${on ? "" : "opacity-55"}`}
                            data-testid={`build-tool-row-${t.slug}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleTool(t.slug)}
                              className="mt-0.5 accent-black"
                              data-testid={`build-tool-check-${t.slug}`}
                            />
                            <span className="min-w-0">
                              <span className="font-bold text-[12px]">{stripClass(t.title, t.asset_class)}</span>
                              {t.asset_class && (
                                <span className="text-[#888] text-[11px]"> [{t.asset_class}]</span>
                              )}
                              {t.key_feature && (
                                <span className="block text-[11px] text-[#888] truncate">{t.key_feature}</span>
                              )}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <label htmlFor="stack-name" className="block text-[12px] text-[#888] mb-1">
                terminal name <span className="text-[#bbb]">(optional — auto-filled from your search)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <input
                  id="stack-name"
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !busy) build();
                  }}
                  maxLength={80}
                  placeholder="e.g. small-cap research"
                  className="tv-input text-[13px] flex-1 min-w-[200px]"
                  data-testid="build-name-input"
                />
                <button
                  type="button"
                  disabled={busy || (tools.length > 0 && selected.size === 0)}
                  onClick={build}
                  className="bg-black text-white px-3 py-2 text-[12px] font-bold hover:bg-[#222] transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="build-create"
                >
                  [ save {tools.length > 0 ? `${selected.size} ` : ""}to my terminal → ]
                </button>
              </div>
            </div>
          </div>
        )}

        {phase === "human" && created.length > 0 && (
          <div data-testid="build-human">
            <div className="border-2 border-black p-4">
              <div className="font-bold mb-1">
                {">"}_ {multi ? `created ${created.length} terminals` : "your terminal is ready"}
              </div>
              {multi && (
                <p className="text-[#555] leading-snug mb-3">
                  Your picks spanned multiple asset classes, so they split into
                  one terminal per class:
                </p>
              )}
              <div className="flex flex-col gap-2 mb-1">
                {created.map((t) => {
                  const base =
                    (t.title && t.asset_class && t.title.endsWith(` · ${t.asset_class}`)
                      ? t.title.slice(0, -(` · ${t.asset_class}`.length))
                      : t.title) || t.asset_class;
                  const showClass = base !== t.asset_class;
                  return (
                    <Link
                      key={t.id}
                      to={`/terminal/${t.id}`}
                      onClick={() => {
                        try { localStorage.setItem(ACTIVE_KEY, t.id); } catch { /* ignore */ }
                      }}
                      className="text-left border border-black px-3 py-2 text-[12px] hover:bg-[#f3f3f3] transition-colors no-underline text-black flex items-center justify-between gap-2"
                      data-testid={`build-open-${t.id}`}
                    >
                      <span className="font-bold truncate">{base}</span>
                      <span className="text-[#888] whitespace-nowrap">
                        {showClass ? `${t.asset_class} · ` : ""}{t.tool_count} tool{t.tool_count === 1 ? "" : "s"} →
                      </span>
                    </Link>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#888] mt-2">
                Your terminals now ride along at the top of every page — switch
                between them from the bar.
              </p>
            </div>

            <DesktopTerminalCard />
          </div>
        )}

        {phase === "agent" && (
          <div data-testid="build-agent">
            <div className="border-2 border-black p-4">
              <div className="font-bold mb-1">{">"}_ Export Agent Toolkit</div>
              <p className="text-[#555] leading-snug mb-3">
                A factual tool list for your AI agent — a map, not access. Each tool
                is a third-party subscription. No prompts, no credentials.
                ({slugs.length} tool{slugs.length === 1 ? "" : "s"})
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => dl("json")}
                  className="bg-black text-white px-3 py-2 text-[12px] font-bold hover:bg-[#222] transition-colors"
                  data-testid="build-dl-json"
                >
                  [ download stack.json ]
                </button>
                <button
                  type="button"
                  onClick={() => dl("md")}
                  className="border border-black bg-white text-black px-3 py-2 text-[12px] font-bold hover:bg-[#f3f3f3] transition-colors"
                  data-testid="build-dl-md"
                >
                  [ download stack.md ]
                </button>
                <button
                  type="button"
                  onClick={() => dl("readme")}
                  className="border border-black bg-white text-black px-3 py-2 text-[12px] font-bold hover:bg-[#f3f3f3] transition-colors"
                  data-testid="build-dl-readme"
                >
                  [ download AGENT-README.md ]
                </button>
              </div>
            </div>
            <div className="mt-4 text-[#666]">
              <Link to="/" className="tv-link">← back to the index</Link>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
